/**
 * Product Variant Service
 * Handles all variant-related API calls including attributes, options, and variants
 */

import apiService from 'core/services/apiClient';
import logger from 'core/utils/logger';
import type {
  VariantAttribute,
  VariantOption,
  ProductVariant,
  CreateVariantAttributeRequest,
  CreateVariantOptionRequest,
  CreateProductVariantRequest,
  GenerateVariantsRequest,
  UpdateVariantStockRequest,
  BulkUpdateVariantItem,
} from '../types';

// API path constants
const API_PATHS = {
  PRODUCTS: 'web/master/product',
};

class VariantService {
  // ==================== ATTRIBUTE MANAGEMENT ====================

  /**
   * Create a new variant attribute for a product
   */
  async createAttribute(productId: string, data: CreateVariantAttributeRequest): Promise<VariantAttribute> {
    try {
      logger.info('Creating variant attribute', { productId, data });
      const response = await apiService.post(`${API_PATHS.PRODUCTS}/${productId}/attributes`, data);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to create variant attribute', error);
      throw error;
    }
  }

  /**
   * Get all attributes for a product
   */
  async getAttributes(productId: string): Promise<VariantAttribute[]> {
    try {
      logger.info('Fetching variant attributes', { productId });
      const response = await apiService.get(`${API_PATHS.PRODUCTS}/${productId}/attributes`);
      return response?.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch variant attributes', error);
      throw error;
    }
  }

  /**
   * Update an attribute
   */
  async updateAttribute(attributeId: string, data: Partial<VariantAttribute>): Promise<VariantAttribute> {
    try {
      logger.info('Updating variant attribute', { attributeId, data });
      const response = await apiService.put(`${API_PATHS.PRODUCTS}/attributes/${attributeId}`, data);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update variant attribute', error);
      throw error;
    }
  }

  /**
   * Delete an attribute
   */
  async deleteAttribute(attributeId: string): Promise<void> {
    try {
      logger.info('Deleting variant attribute', { attributeId });
      await apiService.delete(`${API_PATHS.PRODUCTS}/attributes/${attributeId}`);
    } catch (error) {
      logger.error('Failed to delete variant attribute', error);
      throw error;
    }
  }

  // ==================== OPTION MANAGEMENT ====================

  /**
   * Create a new option for an attribute
   */
  async createOption(attributeId: string, data: CreateVariantOptionRequest): Promise<VariantOption> {
    try {
      logger.info('Creating variant option', { attributeId, data });
      const response = await apiService.post(`${API_PATHS.PRODUCTS}/attributes/${attributeId}/options`, data);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to create variant option', error);
      throw error;
    }
  }

  /**
   * Update an option
   */
  async updateOption(optionId: string, data: Partial<VariantOption>): Promise<VariantOption> {
    try {
      logger.info('Updating variant option', { optionId, data });
      const response = await apiService.put(`${API_PATHS.PRODUCTS}/options/${optionId}`, data);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update variant option', error);
      throw error;
    }
  }

  /**
   * Delete an option
   */
  async deleteOption(optionId: string): Promise<void> {
    try {
      logger.info('Deleting variant option', { optionId });
      await apiService.delete(`${API_PATHS.PRODUCTS}/options/${optionId}`);
    } catch (error) {
      logger.error('Failed to delete variant option', error);
      throw error;
    }
  }

  // ==================== VARIANT MANAGEMENT ====================

  /**
   * Create a single variant
   */
  async createVariant(productId: string, data: CreateProductVariantRequest): Promise<ProductVariant> {
    try {
      logger.info('Creating product variant', { productId, data });
      const response = await apiService.post(`${API_PATHS.PRODUCTS}/${productId}/variants`, data);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to create product variant', error);
      throw error;
    }
  }

  /**
   * Generate all variant combinations automatically
   */
  async generateVariants(productId: string, data: GenerateVariantsRequest): Promise<{
    created: number;
    existing: number;
    variants: ProductVariant[];
  }> {
    try {
      logger.info('Generating variants', { productId, data });
      const response = await apiService.post(`${API_PATHS.PRODUCTS}/${productId}/variants/generate`, data);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to generate variants', error);
      throw error;
    }
  }

  /**
   * Get all variants for a product
   */
  async getVariants(productId: string): Promise<ProductVariant[]> {
    try {
      logger.info('Fetching product variants', { productId });
      const response = await apiService.get(`${API_PATHS.PRODUCTS}/${productId}/variants`);
      return response?.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch product variants', error);
      throw error;
    }
  }

  /**
   * Get a single variant by ID
   */
  async getVariant(variantId: string): Promise<ProductVariant> {
    try {
      logger.info('Fetching variant', { variantId });
      const response = await apiService.get(`${API_PATHS.PRODUCTS}/variants/${variantId}`);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch variant', error);
      throw error;
    }
  }

  /**
   * Get a variant by SKU
   */
  async getVariantBySku(sku: string): Promise<ProductVariant> {
    try {
      logger.info('Fetching variant by SKU', { sku });
      const response = await apiService.get(`${API_PATHS.PRODUCTS}/variants/sku/${sku}`);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch variant by SKU', error);
      throw error;
    }
  }

  /**
   * Update a variant
   */
  async updateVariant(variantId: string, data: Partial<ProductVariant>): Promise<ProductVariant> {
    try {
      logger.info('Updating variant', { variantId, data });
      const response = await apiService.put(`${API_PATHS.PRODUCTS}/variants/${variantId}`, data);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update variant', error);
      throw error;
    }
  }

  /**
   * Update variant stock
   */
  async updateVariantStock(variantId: string, data: UpdateVariantStockRequest): Promise<ProductVariant> {
    try {
      logger.info('Updating variant stock', { variantId, data });
      const response = await apiService.patch(`${API_PATHS.PRODUCTS}/variants/${variantId}/stock`, data);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update variant stock', error);
      throw error;
    }
  }

  /**
   * Bulk update variants
   */
  async bulkUpdateVariants(variants: BulkUpdateVariantItem[]): Promise<ProductVariant[]> {
    try {
      logger.info('Bulk updating variants', { count: variants.length });
      const response = await apiService.patch(`${API_PATHS.PRODUCTS}/variants/bulk-update`, { variants });
      return response?.data || response || [];
    } catch (error) {
      logger.error('Failed to bulk update variants', error);
      throw error;
    }
  }

  /**
   * Delete a variant
   */
  async deleteVariant(variantId: string): Promise<void> {
    try {
      logger.info('Deleting variant', { variantId });
      await apiService.delete(`${API_PATHS.PRODUCTS}/variants/${variantId}`);
    } catch (error) {
      logger.error('Failed to delete variant', error);
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get all variants with stock info for stock management
   */
  async getVariantsForStockManagement(productId: string): Promise<ProductVariant[]> {
    try {
      const variants = await this.getVariants(productId);
      return variants.filter(v => v.trackInventory);
    } catch (error) {
      logger.error('Failed to fetch variants for stock management', error);
      throw error;
    }
  }
}

export const variantService = new VariantService();
export default variantService;
